import { formatChartDate } from "./date";
import { formatDecimalPlaces } from "./number";
/** The singular or plural form of `noun` for `count` (plural defaults to `+s`). */
export const pluralize = (count, singular, plural = `${singular}s`) => (count === 1 ? singular : plural);
/**
 * A "N noun(s)" count label with grouped thousands, e.g. `1 supplier`,
 * `12 suppliers`. Used for KPI sub-labels across the dashboard surface.
 */
export const formatCountLabel = (count, singular, plural) => `${formatDecimalPlaces(count, 0)} ${pluralize(count, singular, plural)}`;
export const getDocumentName = (prefix, originalName) => `${prefix}.${originalName.split(".").pop()}`;
export const getFileName = (originalName) => `${originalName.split("/").pop()}`;
export const getZipTitle = (date, name) => {
    const safeName = name.replace(/ /g, "_");
    const safeDate = formatChartDate(date).replace(/ /g, "_");
    return `Catchment_Report-${safeName}-${safeDate}`;
};
/**
 * Cleans a string by removing all whitespace and non-alphanumeric characters.
 * Primarily used for sanitizing ABNs before database insertion.
 *
 * @param input - The string to clean (e.g., "87 130 808 276" or "87-130-808-276")
 * @returns The cleaned string with only digits (e.g., "87130808276")
 *
 * @example
 * cleanString("87 130 808 276") // "87130808276"
 * cleanString("87-130-808-276") // "87130808276"
 * cleanString("87\t130\n808\t276") // "87130808276"
 */
export const cleanString = (input) => {
    const strInput = input != null ? String(input) : "";
    // Remove all whitespace (spaces, tabs, newlines, etc.) and non-digit characters
    return strInput.replace(/\s/g, "").replace(/[^\d]/g, "").trim();
};
export const formattedUserName = (userId) => {
    let formattedUserId = userId;
    formattedUserId = formattedUserId.replace(/@/g, "");
    formattedUserId = formattedUserId.replace(/\./g, "");
    formattedUserId = formattedUserId.substring(0, 36);
    return formattedUserId.toLowerCase();
};
export const toTitleCase = (str) => {
    // Check if the string is already in uppercase
    if (str === str.toUpperCase()) {
        // Convert the entire string to lowercase and apply title case transformation
        return str.toLowerCase().replace(/\b\w/g, (char) => {
            return char.toUpperCase();
        });
    }
    else {
        // Apply title case transformation directly
        return str.replace(/\b\w/g, (char) => {
            return char.toUpperCase();
        });
    }
};
/**
 * AP-style minor words: kept lowercase in the middle of a headline, but still
 * capitalized when they lead or close it. Shared with
 * {@link toOrganisationDisplayName}, which needs the same vocabulary to tell a
 * short word ("AND", "THE") from a short acronym ("TFH", "VIC").
 */
const MINOR_WORDS = new Set([
    "a",
    "an",
    "and",
    "as",
    "at",
    "but",
    "by",
    "for",
    "from",
    "in",
    "nor",
    "of",
    "on",
    "or",
    "per",
    "the",
    "to",
    "vs",
    "via",
    "with",
    "is"
]);
/**
 * Initialisms preserved exactly as listed. Anything not here is treated as an
 * ordinary word, so `"SHARE OF TOTAL"` still normalizes to `"Share of Total"`.
 * Extend deliberately.
 */
const HEADLINE_ACRONYMS = new Set([
    "ABN",
    "ACN",
    "ATC",
    "ATSI",
    "CEO",
    "CSV",
    "EBA",
    "EEO",
    "FTE",
    "FY",
    "GST",
    "HR",
    "ID",
    "KPI",
    "LGA",
    "NDIS",
    "OCR",
    "PDF",
    "PPE",
    "QA",
    "RAP",
    "ROI",
    "RTO",
    "SME",
    "TAFE",
    "URL",
    "VET",
    "WHS",
    "XLSX",
    "YTD",
    "BEP",
    "ANZ"
]);
/**
 * Units and short codes whose exact casing is meaningful and can't be inferred.
 * Matched case-sensitively. Units carrying a digit (`tCO₂e`, `m³`) need no entry
 * — they are preserved by rule. Extend deliberately.
 */
const HEADLINE_UNITS = new Set(["pp", "wP", "kWh", "kL", "kg", "t"]);
/** A token without the brackets and punctuation around it: `"(kL)"` → `"kL"`. */
const tokenCore = (token) => token.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9²³¹⁰-₟]+$/g, "");
/**
 * A digit in any of the forms a unit actually writes one — ASCII, subscript
 * (`tCO₂e`) or superscript (`m³`, whose `¹²³` sit apart from the rest of the
 * superscript block). `\d` alone sees only the first, which let the very units
 * rule 2 exists for through to be capitalized into `TCO₂e` / `M³`.
 */
const ANY_DIGIT = /[0-9²³¹⁰-₟]/;
/** Uppercases the first *letter*, so `"+96%"`/`"(draft)"` aren't corrupted. */
const capitalizeFirstLetter = (token) => token.replace(/[A-Za-z]/, (char) => char.toUpperCase());
/**
 * Normalizes inconsistently-cased text into readable headline case, e.g.
 * `"SHARE OF TOTAL"` and `"share of total"` both become `"Share of Total"`.
 *
 * Five rules, applied per whitespace-separated token, in order:
 *
 * 1. a listed acronym or unit (`ATC`, `pp`, `kWh`), bracketed or not, is left
 *    exactly as supplied;
 * 2. so is any token mixing letters and digits, in ASCII, subscript or
 *    superscript form (`tCO2`, `tCO₂e`, `m³`);
 * 3. a {@link MINOR_WORDS} entry is lowercased, unless it is the first or last
 *    word, which are always capitalized;
 * 4. an all-caps word is flattened, so shouting input reads as prose;
 * 5. every other token gets its first *letter* capitalized and is otherwise left
 *    alone — `"non-trade"` → `"Non-trade"`, and numbers pass through untouched.
 *
 * Whitespace is preserved exactly. Unlike {@link toTitleCase} this does not
 * capitalize every word boundary. Casing that must survive belongs in one of the
 * two lists above rather than being inferred.
 */
export const toHeadlineCase = (str) => {
    // Capturing split keeps whitespace runs as tokens, so spacing survives intact.
    const tokens = str.split(/(\s+)/);
    const isWord = (token) => token.trim().length > 0;
    const wordIdx = tokens
        .map((token, i) => (isWord(token) ? i : -1))
        .filter((i) => i >= 0);
    const firstIdx = wordIdx[0];
    const lastIdx = wordIdx[wordIdx.length - 1];
    return tokens
        .map((token, i) => {
        if (!isWord(token))
            return token;
        // Matched without surrounding brackets too, so "(kL)" keeps its casing.
        const core = tokenCore(token);
        if (HEADLINE_ACRONYMS.has(core) || HEADLINE_UNITS.has(core)) {
            return token;
        }
        if (/[A-Za-z]/.test(token) && ANY_DIGIT.test(token))
            return token;
        const isEdgeWord = i === firstIdx || i === lastIdx;
        if (!isEdgeWord && MINOR_WORDS.has(token.toLowerCase())) {
            return token.toLowerCase();
        }
        const isAllCaps = token === token.toUpperCase();
        return capitalizeFirstLetter(isAllCaps ? token.toLowerCase() : token);
    })
        .join("");
};
/**
 * Short legal-form words that read as ordinary words when de-shouted, so they
 * are exempt from the short-token acronym guard below: "BLOGGS CONSTRUCTION PTY
 * LTD" should read "Bloggs Construction Pty Ltd", not "Bloggs Construction PTY
 * LTD".
 *
 * Deliberately excludes NL, LLC and PLC, which convention keeps capitalised —
 * "Mining Ventures NL", not "Mining Ventures Nl".
 */
const LEGAL_FORM_WORDS = new Set(["pty", "ltd", "inc", "co"]);
/** Letters only — "(VIC)" counts as 3, "R&S" as 2, "3020" as 0. */
const letterCount = (token) => (token.match(/[A-Za-z]/g) ?? []).length;
/**
 * Title-cases one already-lowercased word, capitalizing after a hyphen so
 * "smith-watson" reads "Smith-Watson", and after an apostrophe only when the
 * part before it is a one- or two-letter prefix. That split is what separates
 * "O'NEILL" -> "O'Neill" from "EGAN'S" -> "Egan's".
 */
const capitalizeNameWord = (lower) => lower
    .replace(/(^|-)([a-z])/g, (_m, sep, char) => sep + char.toUpperCase())
    .replace(/^([A-Za-z]{1,2})'([a-z])/, (_m, prefix, char) => `${prefix}'${char.toUpperCase()}`);
/**
 * Renders an organisation name for display, fixing SHOUTED entries without
 * touching casing anyone chose deliberately.
 *
 * A name is only rewritten when it contains **no lowercase letter at all**.
 * "BLOGGS CONSTRUCTION" becomes "Bloggs Construction", while "BHP Billiton",
 * "iiNet" and "Co-operative Bulk Handling" are returned untouched — the moment
 * one lowercase letter appears, the casing is treated as intentional.
 *
 * Within a shouted name:
 *
 * - a single token of five letters or fewer is left alone, because it is far
 *   more likely an acronym than a shouted word ("AHSS", "BMD", "TGSPA");
 * - a minor word is lowercased mid-name and capitalized at either end, so
 *   "SMITH AND SONS" reads "Smith and Sons";
 * - any other token of three letters or fewer keeps its capitals, which is what
 *   protects state codes and initialisms ("(VIC)", "TFH", "I&D");
 * - everything else is title-cased.
 *
 * Deliberately conservative: it would rather leave "ACME" shouting than turn
 * "FCAM" into "Fcam". Distinguishing a four-letter acronym from a four-letter
 * word needs a dictionary, so the tie is broken towards leaving input alone.
 */
export const toOrganisationDisplayName = (name) => {
    const trimmed = (name ?? "").trim();
    // Any lowercase letter means the casing was chosen, not shouted.
    if (trimmed.length === 0 || /[a-z]/.test(trimmed))
        return trimmed;
    // Capturing split keeps whitespace runs as tokens, so spacing survives.
    const tokens = trimmed.split(/(\s+)/);
    const isWord = (token) => token.trim().length > 0;
    const wordIdx = tokens
        .map((token, i) => (isWord(token) ? i : -1))
        .filter((i) => i >= 0);
    // A lone short token is an acronym, not a shouted name.
    if (wordIdx.length === 1 && letterCount(trimmed) <= 5)
        return trimmed;
    const firstIdx = wordIdx[0];
    const lastIdx = wordIdx[wordIdx.length - 1];
    return tokens
        .map((token, i) => {
        if (!isWord(token))
            return token;
        const isMinor = MINOR_WORDS.has(token.toLowerCase());
        const isEdgeWord = i === firstIdx || i === lastIdx;
        if (isMinor) {
            return isEdgeWord
                ? capitalizeNameWord(token.toLowerCase())
                : token.toLowerCase();
        }
        // Short and not a word we know: initialism, state code, or initials.
        const bare = token.replace(/[^A-Za-z]/g, "").toLowerCase();
        if (letterCount(token) <= 3 && !LEGAL_FORM_WORDS.has(bare))
            return token;
        return capitalizeNameWord(token.toLowerCase());
    })
        .join("");
};
/**
 * The label to render for an organisation: its canonical name where the
 * database derived one, with shouted casing normalised, falling back to the
 * legal name.
 *
 * The fallback matters — `canonicalName` is absent on organisations built in
 * the client, and on any response predating the derived column.
 */
export const resolveOrganisationDisplayName = (organisationName, canonicalName) => {
    const canonical = (canonicalName ?? "").trim();
    const legal = (organisationName ?? "").trim();
    return toOrganisationDisplayName(canonical.length > 0 ? canonical : legal);
};
export const cleanFileName = (input) => {
    return input.replace(/[^a-zA-Z0-9._-]/g, "_");
};
export const resolveAuthorName = (firstName, lastName) => {
    const name = `${firstName ?? ""} ${lastName ?? ""}`.trim();
    return name.length > 0 ? name : undefined;
};
